import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PolarArea } from 'react-chartjs-2';
import instructChar from '../images/instruct-n.webp';
import typingSound from '../sounds/escribir.mp3';

const Chevron = ({ open }) => (
    <svg width="11" height="7" viewBox="0 0 11 7" fill="none" style={{ transition: 'transform .2s ease', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', flexShrink: 0 }}>
        <path d="M1 1l4.5 4.5L10 1" stroke="rgba(201,169,110,0.70)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const Check = () => (
    <svg width="11" height="9" viewBox="0 0 11 9" fill="none" style={{ flexShrink: 0 }}>
        <path d="M1 4.5l3 3 6-7" stroke="#c9a96e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
);

const Selector = ({ opciones, seleccion, onSeleccionChange, placeholder }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const selectedLabel = opciones?.find(o => o.id === seleccion)?.nombre || '';

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleSelect = (id) => { onSeleccionChange(id); setOpen(false); };

    return (
        <div className="nt-dropdown" ref={ref}>
            <button
                type="button"
                className={`nt-dropdown-trigger${open ? ' nt-dropdown-open' : ''}`}
                onClick={() => setOpen(o => !o)}
            >
                <span className={`nt-dropdown-val${!seleccion ? ' nt-dropdown-placeholder' : ''}`}>
                    {selectedLabel || placeholder}
                </span>
                <Chevron open={open} />
            </button>
            {open && opciones?.length > 0 && (
                <div className="nt-dropdown-menu">
                    {opciones.map((opcion) => (
                        <button
                            key={opcion.id}
                            type="button"
                            className={`nt-dropdown-item${seleccion === opcion.id ? ' nt-dropdown-item-selected' : ''}`}
                            onClick={() => handleSelect(opcion.id)}
                        >
                            <span>{opcion.nombre}</span>
                            {seleccion === opcion.id && <Check />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const WINE_COLORS_MAP = {
    'pálido': '#e8e0cc', 'medio': '#c9a96e', 'profundo': '#b04070',
    'verde limón': '#b8d830', 'amarillo limón': '#e8d430', 'dorado': '#c9a050',
    'ámbar': '#cc7830', 'rojo violáceo': '#c840a8', 'cereza': '#d83060',
    'rubí': '#c82050', 'granate': '#a01840', 'caoba': '#8a2028',
};

const NoteText = ({ fullText, color0, color1, intensity, primary, secondary, tertiary, taste_a, taste_b, taste_c, taste_d, taste_e }) => {
    const wineColor = color1 ? (WINE_COLORS_MAP[color1.toLowerCase()] || '#c9a96e') : '#c9a96e';
    const magenta = 'rgba(225,75,148,0.90)';

    // Strip <highlight> tags that come from i18next (they render literally)
    const cleanText = fullText.replace(/<\/?highlight>/g, '');

    const colorMap = {
        [color1]:   wineColor,
        [color0]:   wineColor,
        [intensity]: magenta,
        [primary]:   magenta,
        [secondary]: magenta,
        [tertiary]:  magenta,
        [taste_a]:  '#c9a96e',
        [taste_b]:  '#d4a43e',
        [taste_c]:  '#b03030',
        [taste_d]:  '#7a2040',
        [taste_e]:  '#1d9a78',
    };

    const values = Object.keys(colorMap).filter(Boolean).sort((a, b) => b.length - a.length);
    if (!values.length) return <>{cleanText}</>;

    const pattern = new RegExp(`(${values.map(v => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
    const parts = cleanText.split(pattern);

    return (
        <>
            {parts.map((part, i) =>
                colorMap[part]
                    ? <span key={i} className="nt-note-value" style={{ color: colorMap[part] }}>{part}</span>
                    : part
            )}
        </>
    );
};

const SlideComponent = ({ actual, first, second, onSaveAnswers, setCanGoNext }) => {
    const { t, i18n } = useTranslation();

    const resolveDisplay = (spanishVal, sectionTitle) => {
        if (i18n.language !== 'en' || !test) return spanishVal;
        const section = test.find(q => q.title === sectionTitle);
        const opt = section?.question.find(q => q.option === spanishVal);
        return opt?.option_en || spanishVal;
    };
    const [test, setTest] = useState(null);
    const datosDePrueba = first.question;
    const [selector1, setSelector1] = useState('');
    const [selector2, setSelector2] = useState('');
    const [selector3, setSelector3] = useState('');
    const [sliderValue, setSliderValue] = useState(0);
    const [title, setTitle] = useState(null);
    const [displayedText, setDisplayedText] = useState('');
    const [typingActive, setTypingActive] = useState(false);

    const overlayRef = useRef(null);
    const audioRef = useRef(null);

    const selectedButtonGroup0 = localStorage.getItem('selected0');
    const selectedButtonGroup1 = localStorage.getItem('selected1');
    const selectedButtonGroup2 = localStorage.getItem('selected2');
    const selectedButtonGroup3 = (JSON.parse(localStorage.getItem('selected3') || '[]'));
    const selectedButtonGroup4 = (JSON.parse(localStorage.getItem('selected4') || '[]'));
    const selectedButtonGroup5 = (JSON.parse(localStorage.getItem('selected5') || '[]'));
    const selectedButtonGroup6 = JSON.parse(localStorage.getItem('selected6') || '{}');

    useEffect(() => {
        if (setCanGoNext) setCanGoNext(selector1 !== '' && selector2 !== '' && selector3 !== '' && sliderValue > 0);
    }, [selector1, selector2, selector3, sliderValue, setCanGoNext]);

    useEffect(() => {
        const currentTest = JSON.parse(localStorage.getItem('currentTest'));
        const savedTest = JSON.parse(localStorage.getItem('currentTest') || '[]');
        setTitle(savedTest.title);
        if (currentTest) fetchTestDetails(currentTest.id);
    }, []);

    const fetchTestDetails = (testId) => {
        const xhr = new XMLHttpRequest();
        const url = `${process.env.REACT_APP_API_URL}/getTest/${testId}`;
        xhr.open("GET", url, true);
        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4 && xhr.status === 200) {
                const data = JSON.parse(xhr.responseText);
                setTest(data.object);
            }
        };
        xhr.send();
    };

    useEffect(() => {
        const overlay = overlayRef.current;
        if (!overlay) return;
        const observer = new MutationObserver(() => {
            if (overlay.style.display !== 'none') {
                setDisplayedText('');
                setTypingActive(true);
            }
        });
        observer.observe(overlay, { attributes: true, attributeFilter: ['style'] });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!typingActive) return;
        const fullText = t('note.bubble');
        if (displayedText.length >= fullText.length) {
            setTypingActive(false);
            if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
            return;
        }
        if (audioRef.current && audioRef.current.paused) {
            audioRef.current.volume = 0.3;
            audioRef.current.loop = true;
            audioRef.current.play().catch(() => {});
        }
        const timeout = setTimeout(() => {
            setDisplayedText(fullText.slice(0, displayedText.length + 1));
        }, 28);
        return () => clearTimeout(timeout);
    }, [typingActive, displayedText, t]);

    const handleQuitInstructions = () => {
        const instructionsDiv = overlayRef.current;
        if (instructionsDiv) instructionsDiv.style.display = 'none';
        setTypingActive(false);
        if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    };

    const handleSelector1Change = (value) => { setSelector1(value); setSelector2(''); setSelector3(''); };
    const handleSelector2Change = (value) => { setSelector2(value); setSelector3(''); };

    const opciones1 = datosDePrueba.map(d => ({ id: d.variedad, nombre: d.variedad }));
    const opciones2 = selector1
        ? datosDePrueba.find(d => d.variedad === selector1)?.paises.map(p => ({ id: p.pais, nombre: p.pais }))
        : [];
    const opciones3 = selector2
        ? datosDePrueba.find(d => d.variedad === selector1)?.paises.find(p => p.pais === selector2)?.regiones.map(r => ({ id: r.region, nombre: r.region }))
        : [];

    const chartData = {
        labels: ['', '', '', '', ''],
        datasets: [
            { label: t('taste.acidity_label'),   data: [selectedButtonGroup6?.b + 1 || 0, 0, 0, 0, 0], backgroundColor: 'rgba(210,164,62,0.45)',  borderColor: 'rgba(210,164,62,0.85)',  borderWidth: 1 },
            { label: t('taste.tannin_label'),    data: [0, selectedButtonGroup6?.c + 1 || 0, 0, 0, 0], backgroundColor: 'rgba(139,26,26,0.45)',   borderColor: 'rgba(139,26,26,0.85)',   borderWidth: 1 },
            { label: t('taste.body_label'),      data: [0, 0, selectedButtonGroup6?.d + 1 || 0, 0, 0], backgroundColor: 'rgba(107,30,61,0.45)',   borderColor: 'rgba(107,30,61,0.85)',   borderWidth: 1 },
            { label: t('taste.finish_label'),    data: [0, 0, 0, selectedButtonGroup6?.e + 1 || 0, 0], backgroundColor: 'rgba(29,154,120,0.45)',  borderColor: 'rgba(29,154,120,0.85)',  borderWidth: 1 },
            { label: t('taste.sweetness_label'), data: [0, 0, 0, 0, selectedButtonGroup6?.a + 1 || 0], backgroundColor: 'rgba(201,169,110,0.45)', borderColor: 'rgba(201,169,110,0.85)', borderWidth: 1 },
        ],
    };

    const chartOptions = {
        scales: {
            r: {
                angleLines: { color: 'rgba(232,220,196,0.18)' },
                grid:        { color: 'rgba(232,220,196,0.10)' },
                suggestedMin: 0,
                suggestedMax: 3,
                ticks: { display: false },
                pointLabels: { display: false, font: { size: 0 } },
            },
        },
        plugins: {
            legend: false,
            tooltip: {
                backgroundColor: 'rgba(20,5,15,0.92)',
                titleColor: '#e8dcc4',
                bodyColor: '#c9a96e',
                borderColor: 'rgba(232,220,196,0.18)',
                borderWidth: 1,
                padding: 8,
                callbacks: { label: (ctx) => ctx.dataset.label },
            },
        },
        animation: { duration: 400 },
    };

    const sweetness = t('taste.sweetness', { returnObjects: true });
    const acidity   = t('taste.acidity',   { returnObjects: true });
    const tannin    = t('taste.tannin',    { returnObjects: true });
    const body      = t('taste.body',      { returnObjects: true });
    const finish    = t('taste.finish',    { returnObjects: true });

    const taste_a = typeof selectedButtonGroup6.a === 'number' ? sweetness[selectedButtonGroup6.a] : '';
    const taste_b = typeof selectedButtonGroup6.b === 'number' ? acidity[selectedButtonGroup6.b]   : '';
    const taste_c = typeof selectedButtonGroup6.c === 'number' ? tannin[selectedButtonGroup6.c]    : '';
    const taste_d = typeof selectedButtonGroup6.d === 'number' ? body[selectedButtonGroup6.d]      : '';
    const taste_e = typeof selectedButtonGroup6.e === 'number' ? finish[selectedButtonGroup6.e]    : '';

    const RangoInput = () => {
        const [valor, setValor] = useState(sliderValue);

        useEffect(() => { setValor(sliderValue); }, [sliderValue]);

        return (
            <div className="nt-price-wrap">
                <input
                    className="nt-price-range"
                    type="range"
                    id="rango"
                    name="rango"
                    min="0"
                    max="100"
                    value={valor}
                    onChange={(e) => setValor(e.target.value)}
                    onMouseUp={(e) => setSliderValue(e.target.value)}
                    onTouchEnd={(e) => setSliderValue(e.target.value)}
                />
                <span className="nt-price-value">{valor}&euro;</span>
            </div>
        );
    };

    const saveAnswers = async () => {
        const userLogged = JSON.parse(localStorage.getItem('userLogged'));
        const currentTest = JSON.parse(localStorage.getItem('currentTest'));
        if (!userLogged || !currentTest) return;

        const parseJSON = (value) => { try { return JSON.parse(value); } catch (e) { return value; } };

        const sbg0 = parseJSON(localStorage.getItem('selected0'));
        const sbg1 = parseJSON(localStorage.getItem('selected1'));
        const sbg2 = parseJSON(localStorage.getItem('selected2'));
        const sbg3 = parseJSON(localStorage.getItem('selected3'));
        const sbg4 = parseJSON(localStorage.getItem('selected4'));
        const sbg5 = parseJSON(localStorage.getItem('selected5'));
        const sbg6 = parseJSON(localStorage.getItem('selected6'));
        const sbg7 = { selector1, selector2, selector3 };
        const sbg8 = sliderValue;

        const { visual, visualStats }       = calculateVisualStats(test, sbg0, sbg1);
        const { olfactory, olfactoryStats } = calculateOlfactoryStats(test, sbg2, sbg3, sbg4, sbg5);
        const { taste, tasteStats }         = calculateTasteStats(test, sbg6);
        const { variety, varietyStats, countryStats, regionStats } = calculateVarietyStats(test, sbg7);
        const { price, priceStats }         = calculatePriceStats(test, sbg8);

        const payload = {
            object: { selectedButtonGroup0: sbg0, selectedButtonGroup1: sbg1, selectedButtonGroup2: sbg2, selectedButtonGroup3: sbg3, selectedButtonGroup4: sbg4, selectedButtonGroup5: sbg5, selectedButtonGroup6: sbg6, selectedButtonGroup7: sbg7, selectedButtonGroup8: sbg8 },
            user_id: userLogged.id,
            test_id: currentTest.id,
            visual, visual_stats: visualStats,
            olfactory, olfactory_stats: olfactoryStats,
            taste, taste_stats: tasteStats,
            variety, variety_stats: varietyStats,
            country_stats: countryStats, region_stats: regionStats,
            price, price_stats: priceStats,
        };

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/saveAnswer`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error('Network response was not ok');
            ['currentTest','selected0','selected1','selected2','selected3','selected4','selected5','selected6'].forEach(k => localStorage.removeItem(k));
            window.location.href = '/choose-wine';
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const calculateVisualStats = (testObject, selectedOption0, selectedOption1) => {
        const intensityOfColor = testObject.find(q => q.title === "Intensidad del color");
        const colorOfWine = testObject.find(q => q.title === "Color del vino");
        let visual = 0;
        intensityOfColor.question.forEach(opt => { if (opt.option === selectedOption0) visual += opt.points; });
        colorOfWine.question.forEach(opt => { if (opt.option === selectedOption1) visual += opt.points; });
        return { visual, visualStats: (visual / 3).toFixed(2) };
    };

    const calculateOlfactoryStats = (testObject, selectedOption2, selectedOptions3, selectedOptions4, selectedOption5) => {
        const intensityOfAroma = testObject.find(q => q.title === "Intensidad del aroma");
        const primaryAromas = testObject.find(q => q.title === "Aromas primarios");
        const secondaryAromas = testObject.find(q => q.title === "Aromas secundarios");
        const tertiaryAromas = testObject.find(q => q.title === "Aromas terciarios");
        let olfactory = 0;
        intensityOfAroma.question.forEach(opt => { if (opt.option === selectedOption2) olfactory += opt.points; });
        primaryAromas.question.forEach(opt => { if (selectedOptions3.includes(opt.option)) olfactory += opt.points; });
        secondaryAromas.question.forEach(opt => { if (selectedOptions4.includes(opt.option)) olfactory += opt.points; });
        tertiaryAromas.question.forEach(opt => { if (selectedOption5.includes(opt.option)) olfactory += opt.points; });
        return { olfactory, olfactoryStats: (olfactory / 8).toFixed(2) };
    };

    const calculateTasteStats = (testObject, selectedOptions6) => {
        const questions = ['Dulzor','Acidez','Tanino','Cuerpo','Final'].map(title => testObject.find(q => q.title === title));
        const keys = ['a','b','c','d','e'];
        let taste = 0;
        questions.forEach((q, i) => { q.question.forEach(opt => { if (Number(opt.option) === selectedOptions6[keys[i]] + 1) taste += opt.points; }); });
        return { taste, tasteStats: (taste / 5).toFixed(2) };
    };

    const calculateVarietyStats = (testObject, selectedVariety) => {
        const varietyQuestion = testObject.find(q => q.title === "Variedad");
        let varietyPoints = 0, countryPoints = 0, regionPoints = 0;
        const selectedVarietyObject = varietyQuestion.question.find(v => v.variedad === selectedVariety.selector1);
        if (selectedVarietyObject) {
            varietyPoints = selectedVarietyObject.points;
            const selectedCountry = selectedVarietyObject.paises.find(p => p.pais === selectedVariety.selector2);
            if (selectedCountry) {
                countryPoints = selectedCountry.points;
                const selectedRegion = selectedCountry.regiones.find(r => r.region === selectedVariety.selector3);
                if (selectedRegion) regionPoints = selectedRegion.points;
            }
        }
        return { variety: varietyPoints + countryPoints + regionPoints, varietyStats: (varietyPoints / 12).toFixed(2), countryStats: (countryPoints / 3).toFixed(2), regionStats: (regionPoints / 5).toFixed(2) };
    };

    const calculatePriceStats = (testObject, selectedPrice) => {
        const priceQuestion = testObject.find(q => q.title === "Precio");
        const selectedOption = priceQuestion.question.find(opt => Number(opt.option) === Number(selectedPrice));
        const price = selectedOption ? selectedOption.points : 0;
        return { price, priceStats: price / 4 };
    };

    useEffect(() => {
        if (onSaveAnswers) onSaveAnswers(saveAnswers);
    }, [selector1, selector2, selector3, sliderValue]);

    const selectors = [
        { label: t('note.variety'), options: opciones1, value: selector1, onChange: handleSelector1Change },
        { label: t('note.country'), options: opciones2 || [], value: selector2, onChange: handleSelector2Change },
        { label: t('note.region'),  options: opciones3 || [], value: selector3, onChange: setSelector3 },
    ];

    return (
        <div className="slide-container">
            <audio ref={audioRef} src={typingSound} />
            <div className="overlay" id="instructions-div" ref={overlayRef}>
                <div className="vi-instruct-scene">
                    <img src={instructChar} alt="" className="vi-instruct-char" />
                    <div className="vi-instruct-bubble-wrap">
                        <div className="vi-bubble-row">
                            <svg className="vi-tail-svg" width="28" height="20" viewBox="0 0 28 20">
                                <polygon points="28,0 28,20 0,10" fill="#fff" stroke="#111" strokeWidth="3" strokeLinejoin="round"/>
                            </svg>
                            <div className="vi-bubble-shell">
                                <div className="vi-instruct-bubble">
                                    <p className="vi-instruct-text">{displayedText}<span className="vi-instruct-cursor">|</span></p>
                                </div>
                                <button className="vi-instruct-close" onClick={handleQuitInstructions} aria-label={t('common.close')}>×</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="ts-phase-icon" role="img" aria-label={t('note.title')}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round">
                    {/* Stem + tendril */}
                    <line x1="12" y1="5.2" x2="12" y2="2.5" strokeWidth="1.5"/>
                    <path d="M12 2.5 Q15.5 1 17.5 3"/>
                    {/* Row 1 — 3 grapes */}
                    <circle cx="8"  cy="8"    r="2.3"/>
                    <circle cx="12" cy="8"    r="2.3"/>
                    <circle cx="16" cy="8"    r="2.3"/>
                    {/* Row 2 — 2 grapes */}
                    <circle cx="10" cy="12.1" r="2.3"/>
                    <circle cx="14" cy="12.1" r="2.3"/>
                    {/* Row 3 — 1 grape (tip of bunch) */}
                    <circle cx="12" cy="16.2" r="2.3"/>
                </svg>
                <span className="ts-phase-icon-tip">{t('note.title')}</span>
            </div>
            <div className="actual-wine">{title}</div>

            <div className="nt-phase">
                <div className="nt-title"><span>{t('note.title')}</span></div>

                <div className="nt-content">
                    <div className="nt-left">
                        <div className="nt-note-text">
                            <NoteText
                                fullText={t('note.tasting_text', {
                                    color1: resolveDisplay(selectedButtonGroup1, "Color del vino"),
                                    color0: resolveDisplay(selectedButtonGroup0, "Intensidad del color"),
                                    intensity: resolveDisplay(selectedButtonGroup2, "Intensidad del aroma"),
                                    primary: selectedButtonGroup3.map(v => resolveDisplay(v, "Aromas primarios")).join(", "),
                                    secondary: selectedButtonGroup4.map(v => resolveDisplay(v, "Aromas secundarios")).join(", "),
                                    tertiary: selectedButtonGroup5.map(v => resolveDisplay(v, "Aromas terciarios")).join(", "),
                                    taste_a, taste_b, taste_c, taste_d, taste_e,
                                })}
                                color0={resolveDisplay(selectedButtonGroup0, "Intensidad del color")}
                                color1={resolveDisplay(selectedButtonGroup1, "Color del vino")}
                                intensity={resolveDisplay(selectedButtonGroup2, "Intensidad del aroma")}
                                primary={selectedButtonGroup3.map(v => resolveDisplay(v, "Aromas primarios")).join(", ")}
                                secondary={selectedButtonGroup4.map(v => resolveDisplay(v, "Aromas secundarios")).join(", ")}
                                tertiary={selectedButtonGroup5.map(v => resolveDisplay(v, "Aromas terciarios")).join(", ")}
                                taste_a={taste_a} taste_b={taste_b} taste_c={taste_c}
                                taste_d={taste_d} taste_e={taste_e}
                            />
                        </div>
                        <div className="nt-chart">
                            <PolarArea data={chartData} options={chartOptions} />
                        </div>
                    </div>

                    <div className="nt-right">
                        {selectors.map(({ label, options, value, onChange }) => (
                            <div key={label} className="nt-select-row">
                                <div className="nt-select-label">{label}</div>
                                <Selector
                                    opciones={options}
                                    seleccion={value}
                                    onSeleccionChange={onChange}
                                    placeholder={t('note.select_placeholder')}
                                />
                            </div>
                        ))}

                        <div className="nt-select-row">
                            <div className="nt-select-label">{t('note.price')}</div>
                            <RangoInput />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SlideComponent;
